// Телеметрия живых забегов: исходы, причины смерти, длительность, сессии.
//
// Раздел 2 контракта требует трёх вещей, которые легко потерять молча:
// понимать обе схемы (в v=1 нет `cheats`), снимать точные дубликаты и
// показывать снятое числом, исключать `cheats=1` из статистики, оставляя
// записи видимыми списком. Всё три показаны на странице явно.

import { useEffect, useMemo, useState } from 'react';
import { useStore } from '../data/store';
import { loadTelemetry } from '../data/source';
import { Callout, OriginTag, Panel, Tile } from '../components/Ui';
import { ShareBar } from '../components/Charts';
import { summarizeTelemetry } from '../lib/telemetry';
import { describe, histogram } from '../lib/stats';
import { Histogram } from '../components/Charts';
import { num, percent, seconds, stamp } from '../lib/format';
import type { TelemetryReport } from '../lib/types';

const OUTCOME_COLORS: Record<string, string> = {
  Died: 'var(--series-d)',
  Evacuated: 'var(--series-a)',
  InProgress: 'var(--series-e)',
};

const CAUSE_COLORS: Record<string, string> = {
  Actor: 'var(--series-d)',
  World: 'var(--series-c)',
  None: 'var(--series-a)',
  Unknown: 'var(--series-b)',
};

export function Telemetry() {
  const store = useStore();
  const [report, setReport] = useState<TelemetryReport | null>(null);
  const [failure, setFailure] = useState<string | null>(null);

  useEffect(() => {
    const server = store.server;
    if (server === null) return;
    let alive = true;
    void (async () => {
      try {
        const result = await loadTelemetry(server.online);
        if (alive) setReport(result.value);
      } catch (error) {
        if (alive) setFailure(error instanceof Error ? error.message : String(error));
      }
    })();
    return () => {
      alive = false;
    };
  }, [store.server]);

  const summary = useMemo(() => (report ? summarizeTelemetry(report.records) : null), [report]);
  const durations = useMemo(() => (summary ? summary.clean.map((r) => r.sec) : []), [summary]);
  const durationStats = useMemo(() => describe(durations), [durations]);
  // Мемоизация обязательна: массив, собранный прямо в JSX, пересобирает
  // график целиком на каждую перерисовку страницы (см. PlotFigure).
  const durationBins = useMemo(
    () => histogram(durations.map((s) => Math.round(s / 10) * 10), 40),
    [durations],
  );
  const outcomeParts = useMemo(
    () =>
      (summary?.outcomes ?? []).map((o) => ({
        label: o.outcome,
        value: o.count,
        color: OUTCOME_COLORS[o.outcome] ?? 'var(--series-b)',
      })),
    [summary],
  );
  const causeParts = useMemo(
    () =>
      (summary?.causes ?? []).map((c) => ({
        label: c.cause,
        value: c.count,
        color: CAUSE_COLORS[c.cause] ?? 'var(--series-e)',
      })),
    [summary],
  );

  if (failure) {
    return (
      <div className="page">
        <Callout tone="hard" title="Телеметрия не прочиталась">
          <p className="mono">{failure}</p>
        </Callout>
      </div>
    );
  }

  if (report === null || summary === null) {
    return (
      <div className="page">
        <div className="empty">загрузка…</div>
      </div>
    );
  }

  const schemaV1 = report.records.filter((r) => r.version === 1).length;
  const schemaV2 = report.records.filter((r) => r.version === 2).length;
  const schemaV3 = report.records.filter((r) => r.version >= 3).length;

  return (
    <div className="page">
      <div className="page-head">
        <h1>Телеметрия</h1>
        <p>
          Живые забеги из логов. Это единственные данные во всём приложении, полученные от человека
          за клавиатурой, — и единственные, по которым вообще можно говорить о сложности: симуляция
          уровень не проходит (ADR-0023).
        </p>
        <OriginTag origin={report.origin} />
      </div>

      <div className="tiles">
        <Tile
          label="Записей после чистки"
          value={num(summary.clean.length)}
          note={`снято точных дубликатов: ${num(report.duplicatesRemoved)}`}
        />
        <Tile
          label="Дошли до эвакуации"
          value={percent(summary.survivalShare)}
          tone={summary.survivalShare > 0.4 ? 'good' : summary.survivalShare > 0.15 ? 'warn' : 'bad'}
          note={`${num(summary.clean.filter((r) => r.outcome === 'Evacuated').length)} из ${num(summary.clean.length)}`}
        />
        <Tile
          label="Медиана забега"
          value={seconds(summary.medianSec)}
          note={`p10 ${seconds(durationStats.p10)} · p90 ${seconds(durationStats.p90)} · игровое время при 30 Гц`}
        />
        <Tile label="Сессий" value={num(summary.sessions.length)} note="разрыв больше 30 минут — новая сессия" />
        <Tile
          label="С читами"
          value={num(summary.cheated.length)}
          tone={summary.cheated.length > 0 ? 'warn' : 'plain'}
          note="исключены из статистики, показаны списком ниже"
        />
        <Tile
          label="Схема v=1 / v=2 / v=3"
          value={`${num(schemaV1)} / ${num(schemaV2)} / ${num(schemaV3)}`}
          note={`в v=1 поля cheats нет — ${num(summary.unknownCheats)} записей про читы не знают ничего`}
        />
      </div>

      {summary.v3.length > 0 && (
        <div className="tiles">
          <Tile label="Медиана точности" value={summary.medianAccuracy === null ? 'н/д' : percent(summary.medianAccuracy)} note="попадания / состоявшиеся выстрелы" />
          <Tile label="Патроны в рюкзаке" value={summary.medianAmmo === null ? 'н/д' : num(summary.medianAmmo)} note="медиана на конце забега; магазин отдельно не считается" />
          <Tile label="Здоровье на конце" value={summary.medianHealthShare === null ? 'н/д' : percent(summary.medianHealthShare)} note="медиана доли HP экипажа" />
          <Tile label="Пик угрозы" value={summary.medianThreat === null ? 'н/д' : percent(summary.medianThreat / 1000)} note="медиана максимума за забег" />
        </div>
      )}

      {report.inProgress > 0 && (
        <Callout tone="hard" title="В логе есть записи outcome=InProgress">
          <p>
            Их <b>{num(report.inProgress)}</b>. Таких записей не бывает: забег пишется в лог по
            завершении. Это порча лога, и о ней сказано здесь, а не спрятано в статистике.
          </p>
        </Callout>
      )}

      {report.malformed.length > 0 && (
        <Callout tone="hard" title={`Строк с меткой, но без обязательных полей: ${num(report.malformed.length)}`}>
          {report.malformed.slice(0, 5).map((line) => (
            <p className="mono" key={line}>
              {line}
            </p>
          ))}
        </Callout>
      )}

      <div className="split">
        <Panel title="Исходы">
          <ShareBar parts={outcomeParts} />
          <table style={{ marginTop: 10 }}>
            <thead>
              <tr>
                <th>Исход</th>
                <th className="n">записей</th>
                <th className="n">доля</th>
              </tr>
            </thead>
            <tbody>
              {summary.outcomes.map((o) => (
                <tr key={o.outcome}>
                  <td>{o.outcome}</td>
                  <td className="n">{num(o.count)}</td>
                  <td className="n">{percent(o.share)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Panel>

        <Panel title="Причины смерти">
          <ShareBar parts={causeParts} />
          <table style={{ marginTop: 10 }}>
            <thead>
              <tr>
                <th>Причина</th>
                <th className="n">записей</th>
                <th className="n">доля</th>
                <th>Что это</th>
              </tr>
            </thead>
            <tbody>
              {summary.causes.map((c) => (
                <tr key={c.cause}>
                  <td>{c.cause}</td>
                  <td className="n">{num(c.count)}</td>
                  <td className="n">{percent(c.share)}</td>
                  <td className="dim">
                    {c.cause === 'Actor'
                      ? 'убил враг'
                      : c.cause === 'World'
                        ? 'убил мир: падение, ловушка, среда'
                        : c.cause === 'None'
                          ? 'смерти не было'
                          : 'источник не определён'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Panel>
      </div>

      <Panel title="Длительность забега">
        <Histogram
          bins={durationBins}
          stats={durationStats}
          unit="с"
          color="var(--series-b)"
        />
        <p className="dim" style={{ marginTop: 6, marginBottom: 0 }}>
          Столбик — десять секунд игрового времени. Это не настенное время сессии: секунды выведены
          из тика при 30 Гц, как сказано в разделе 2 контракта.
        </p>
      </Panel>

      <Panel title="Кто убивал" flush>
        {summary.killers.length === 0 ? (
          <div className="empty">ни одной записи с известным убийцей</div>
        ) : (
          <table>
            <thead>
              <tr>
                <th className="n">id актора</th>
                <th className="n">смертей</th>
                <th>Замечание</th>
              </tr>
            </thead>
            <tbody>
              {summary.killers.map((killer) => (
                <tr key={killer.killer}>
                  <td className="n mono">{killer.killer}</td>
                  <td className="n">{num(killer.count)}</td>
                  <td className="dim">
                    номер актора в забеге, а не тип врага: сопоставить его с существом лог не позволяет
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Panel>

      <Panel title="Сессии" flush>
        <div className="scroll">
          <table>
            <thead>
              <tr>
                <th className="n">№</th>
                <th>Начало</th>
                <th>Конец</th>
                <th className="n">забегов</th>
                <th className="n">смертей</th>
                <th className="n">эвакуаций</th>
                <th className="n">медиана</th>
              </tr>
            </thead>
            <tbody>
              {summary.sessions.map((session) => (
                <tr key={session.index}>
                  <td className="n mono">{session.index}</td>
                  <td className="mono">{stamp(session.from)}</td>
                  <td className="mono">{stamp(session.to)}</td>
                  <td className="n">{num(session.runs)}</td>
                  <td className="n bad">{num(session.died)}</td>
                  <td className="n good">{num(session.evacuated)}</td>
                  <td className="n">{seconds(session.medianSec)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Panel>

      {summary.cheated.length > 0 && (
        <Panel title={`Забеги с читами — ${num(summary.cheated.length)}`} flush>
          <div className="scroll">
            <table>
              <thead>
                <tr>
                  <th className="n">seed</th>
                  <th>Исход</th>
                  <th className="n">сек</th>
                  <th>Когда</th>
                </tr>
              </thead>
              <tbody>
                {summary.cheated.map((record) => (
                  <tr key={record.raw}>
                    <td className="n mono">{record.seed}</td>
                    <td>{record.outcome}</td>
                    <td className="n">{num(record.sec, 1)}</td>
                    <td className="mono dim">{stamp(record.at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Panel>
      )}

      <Panel title="Все записи" flush>
        <div className="scroll">
          <table>
            <thead>
              <tr>
                <th className="n">v</th>
                <th className="n">seed</th>
                <th>Исход</th>
                <th>Причина</th>
                <th className="n">убийца</th>
                <th className="n">тик</th>
                <th className="n">сек</th>
                <th className="n">убил</th>
                <th className="n">лут</th>
                <th>акт / уровень</th>
                <th className="n">HP</th>
                <th className="n">патроны</th>
                <th className="n">выстр. / попад.</th>
                <th className="n">лечение</th>
                <th className="n">угроза</th>
                <th className="n">Смотритель</th>
                <th className="n">читы</th>
                <th className="n">сессия</th>
                <th>Когда</th>
              </tr>
            </thead>
            <tbody>
              {report.records.map((record) => (
                <tr key={record.raw}>
                  <td className="n faint">{record.version}</td>
                  <td className="n mono">{record.seed}</td>
                  <td className={record.outcome === 'Evacuated' ? 'good' : 'bad'}>{record.outcome}</td>
                  <td className="dim">{record.cause}</td>
                  <td className="n">{record.killer >= 0 ? record.killer : '—'}</td>
                  <td className="n dim">{num(record.tick)}</td>
                  <td className="n">{num(record.sec, 1)}</td>
                  <td className="n">{num(record.kills)}</td>
                  <td className="n">{num(record.loot)}</td>
                  <td className="dim">{record.act === null ? 'н/д' : `${record.act} / ${record.level ?? 'н/д'}`}</td>
                  <td className="n">{record.hp === null || record.hp < 0 ? 'н/д' : `${record.hp}/${record.hpmax}`}</td>
                  <td className="n">{record.ammo === null || record.ammo < 0 ? 'н/д' : record.ammo}</td>
                  <td className="n">{record.shots === null ? 'н/д' : `${record.shots}/${record.hits}`}</td>
                  <td className="n">{record.heal === null ? 'н/д' : record.heal}</td>
                  <td className="n">{record.threat === null ? 'н/д' : percent(record.threat / 1000)}</td>
                  <td className="n">{record.warden === null ? 'н/д' : `${record.warden} · ${seconds((record.wardenticks ?? 0) / 30)}`}</td>
                  <td className="n">
                    {record.cheats === null ? <span className="faint" title="схема v=1: поля нет">н/д</span> : record.cheats}
                  </td>
                  <td className="n faint">{record.session}</td>
                  <td className="mono dim">{stamp(record.at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Panel>
    </div>
  );
}
